import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent,
    InputLabel,
} from "@mui/material";
import DatePickerComponent from "../../Common/DatePicker";
import Select from "../../Common/Select";
import MultipleSelectJson from "../../Common/MultipleSelectJson";
import { Clear as ClearIcon } from "@mui/icons-material";

import { useGenerateInsuranceReportQuery } from "../../../store/reports/reportsApis";
import { useGetProductsDropdownQuery } from "../../../store/masters/mastersApis";

const InsuranceReport = ({
    reportType,
    filters,
    onFilterUpdate,
    onFilterReset,
    onExport,
    onRefresh,
    onReportDataUpdate,
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Fetch dropdown data
    const { data: productsData } = useGetProductsDropdownQuery();

    const { data: reportData, isLoading, error, refetch } = useGenerateInsuranceReportQuery(localFilters);

    // Update parent with report data whenever it changes
    useEffect(() => {
        if (reportData && onReportDataUpdate) {
            onReportDataUpdate("insurance", reportData);
        }
    }, [reportData, onReportDataUpdate]);

    const handleFilterChange = (field, value) => {
        // Extract values from MultipleSelectJson objects
        let processedValue = value;
        if (field.includes("_ids") && Array.isArray(value)) {
            processedValue = value.map((item) => item.value || item);
            // Remove duplicates
            processedValue = [...new Set(processedValue)];
        }

        const newFilters = { ...localFilters, [field]: processedValue };
        setLocalFilters(newFilters);
        onFilterUpdate(newFilters);
    };

    const handleReset = () => {
        setLocalFilters({
            start_date: null,
            end_date: null,
            payment_status: null,
            product_ids: [],
            format: "json",
        });
        onFilterReset();
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: "warning",
            paid: "success",
            failed: "error",
            cancelled: "secondary",
        };
        return colors[status] || "default";
    };

    return (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="Start Date"
                            value={localFilters.start_date}
                            onChange={(date) => handleFilterChange("start_date", date)}
                            name="start_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="End Date"
                            value={localFilters.end_date}
                            onChange={(date) => handleFilterChange("end_date", date)}
                            name="end_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Payment Status"
                            value={localFilters.payment_status || ""}
                            onChange={(e) => handleFilterChange("payment_status", e.target.value)}
                            name="payment_status"
                            options={[
                                { value: "", label: "All Payment Status" },
                                { value: "pending", label: "Pending" },
                                { value: "paid", label: "Paid" },
                                { value: "failed", label: "Failed" },
                                { value: "cancelled", label: "Cancelled" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Products
                            </InputLabel>
                            <MultipleSelectJson
                                id="product-select"
                                label="Products"
                                value={localFilters.product_ids || []}
                                onChange={(value) => handleFilterChange("product_ids", value)}
                                name="product_ids"
                                options={productsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Format"
                            value={localFilters.format || "json"}
                            onChange={(e) => handleFilterChange("format", e.target.value)}
                            name="format"
                            options={[
                                { value: "json", label: "JSON" },
                                { value: "csv", label: "CSV" },
                                { value: "excel", label: "Excel" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} size="small">
                                Reset
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {isLoading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.data?.message || "Failed to generate insurance report"}
                </Alert>
            )}

            {reportData && !isLoading && (
                <Box>
                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Renewals
                                    </Typography>
                                    <Typography variant="h4">{reportData.summary?.total_renewals || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Premium Amount
                                    </Typography>
                                    <Typography variant="h4">
                                        ₹{reportData.summary?.total_premium_amount?.toLocaleString() || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Payment Status Breakdown */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Payment Status Breakdown
                                </Typography>
                                {reportData.summary?.payment_status_breakdown && (
                                    <Box>
                                        {Object.entries(reportData.summary.payment_status_breakdown).map(
                                            ([status, count]) => (
                                                <Box key={status} display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="body2" textTransform="capitalize">
                                                        {status}
                                                    </Typography>
                                                    <Chip label={count} size="small" color={getStatusColor(status)} />
                                                </Box>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Insurance Renewals Table */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Insurance Renewals ({reportData.insurance_renewals?.length || 0} renewals)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Customer Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Mobile</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Premium Amount</TableCell>
                                        <TableCell>Payment Status</TableCell>
                                        <TableCell>Created Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.insurance_renewals?.map((renewal) => (
                                        <TableRow key={renewal.id}>
                                            <TableCell>{renewal.customer_name}</TableCell>
                                            <TableCell>{renewal.email}</TableCell>
                                            <TableCell>{renewal.mobile}</TableCell>
                                            <TableCell>{renewal.product?.name}</TableCell>
                                            <TableCell>₹{renewal.premium_amount?.toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={renewal.payment_status}
                                                    size="small"
                                                    color={getStatusColor(renewal.payment_status)}
                                                />
                                            </TableCell>
                                            <TableCell>{new Date(renewal.created_at).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}

            {!reportData && !isLoading && !error && (
                <Alert severity="info">No data found for the selected filters.</Alert>
            )}
        </Box>
    );
};

export default InsuranceReport;
